<template>
  <div class="media-selector-wrapper">
    <v-row>
      <v-col cols="12">
        <div :class="classes.root"
             :style="styles.root">
          <div :class="classes.dropzone">
            <vue-dropzone ref="dropzone"
                          id="dropzone"
                          :options="dropzoneOptions"
                          @vdropzone-drag-enter="hovering = true"
                          @vdropzone-drag-leave="hovering = false"
                          @vdropzone-drop="hovering = false"
                          @vdropzone-success="processFile"></vue-dropzone>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-btn color="primary" class="uploadBtn mr-4" :disabled="disabled">Upload File</v-btn>
        <v-dialog v-model="selector.show" scrollable :disabled="disabled">
          <template v-slot:activator="{ on }">
            <v-btn outlined
                   color="primary"
                   :disabled="disabled"
                   v-on="on">Select an Image</v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="headline">Select an Image</span>
              <v-spacer />
              <v-text-field v-model="selector.search"
                            label="Search"
                            append-outer-icon="close"
                            append-icon="search"
                            @click:append-outer="selector.show = false" />
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="height: 80%;">
              <v-container>
                <v-row>
                  <v-col cols="12"
                         sm="6"
                         md="4"
                         lg="3"
                         v-for="image in visibleImages"
                         :key="image.Id">
                    <v-card @click="update(image)">
                      <v-card-title>
                        <p class="d-inline-block text-truncate">
                          {{ image.Title }}
                        </p>
                      </v-card-title>

                      <v-card-subtitle>
                        {{ image.Type }}
                      </v-card-subtitle>

                      <v-img :src="getImage(image.Id)" height="194" />
                    </v-card>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="pagedImages.length > 1">
                    <v-pagination v-model="selector.page"
                                  :total-visible="12"
                                  :length="pagedImages.length" />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
/**
 * Dropzone area with an optional selector to display all media in a given directory
 *
 * Value represents the ID of a Media Record
 * Used on Provider and Location pages to allow the upload or selection of photos for a given record
 */
import vueDropzone from 'vue2-dropzone';
import { arrayChunk, arraySearch } from '@/util/Helpers';
import { friendlyDate } from '@/components/Mixins/FriendlyFields';

export default {
  name: 'MediaSelector',
  components: {
    vueDropzone,
  },
  props: {
    directory: {
      type: String,
      required: true,
    },
    value: [String, Number],
    disabled: Boolean,
    perPage: Number,
  },
  data() {
    return {
      hovering: false,
      dropzoneOptions: {
        url: this.baseUrl(),
        headers: {
          Authorization: `Bearer ${this.$store.getters.user.access_token}`,
        },
        createImageThumbnails: false,
        clickable: this.disabled ? false : [
          '.media-selector__dropzone-area',
          '.uploadBtn',
        ],
      },
      selector: {
        show: false,
        search: null,
        items: [],
        page: 1,
        perPage: this.perPage || 12,
        selected: this.value,
      },
    };
  },
  computed: {
    classes() {
      return {
        root: {
          'media-selector': true,
          'media-selector--hovering': this.hovering,
          'media-selector--disabled': this.disabled,
        },
        cta: {
          'media-selector__selector-cta': true,
        },
        dropzone: {
          'media-selector__dropzone-area': true,
        },
      };
    },
    styles() {
      return {
        root: {
          'background-image': this.preview ? `url(${this.preview})` : 'none',
        },
      };
    },
    preview() {
      return this.selector.selected ? this.getImage(this.selector.selected) : null;
    },
    visibleImages() {
      return this.pagedImages[this.selector.page - 1];
    },
    pagedImages() {
      const filtered = arraySearch(this.selector.items, this.selector.search, 'Title');
      return arrayChunk(filtered, this.selector.perPage);
    },
  },
  watch: {
    'selector.selected': {
      handler(v) {
        this.$emit('input', v);
      },
    },
  },
  methods: {
    friendlyDate,
    baseUrl() {
      return `${this.$http.defaults.baseURL}/Media/${this.directory}`;
    },
    getImage(id) {
      return `${process.env.VUE_APP_API}/Media/${id}`;
    },
    processFile(file, response) {
      const newImages = response.filter((m) => m.Replaced === false);
      if (newImages.length) {
        this.selector.items.push(...newImages);
      }
      this.selector.selected = response[0].Id;
    },
    update(item) {
      this.selector.selected = item.Id;
      this.selector.show = false;
    },
  },
  async created() {
    const response = await this.$store.dispatch('get', this.baseUrl());
    this.selector.items = response.data;
  },
};
</script>
